import React, { useRef, useEffect, useMemo, useState, useCallback } from 'react';
import { StyleSheet, Pressable, Animated, View, Platform, Linking, Easing, ActivityIndicator } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import MessageContentRenderer from '@/components/Messages/MessageContentRenderer';
import MessageProducts from '@/components/Messages/MessageProducts';
import MessageStockQuotes from '@/components/Messages/MessageStockQuotes';
import MessagePlaces from '@/components/Messages/MessagePlaces';
import PropTypes from 'prop-types';
import { Image } from 'expo-image';
import { useModalStore, useChatStore, useAuthStore, useUIStore } from '@/store';
import { useModalHelper } from '@/hooks/useModalHelper';
import useResponsiveness from '@/hooks/useResponsiveness';
import socketManager from '@/lib/socket';
import { NativeThinkingIndicator } from '@/components/Messages/MessageWaiting';

// Rotating phrases for waiting state
const WAITING_PHRASES = [
  'Thinking',
  'Planning next steps',
  'Generating response',
  'Processing',
  'Analyzing',
  'Working on it',
];

const contentContainerStyle = Platform.select({
  web: { minWidth: 0, flexShrink: 1, width: '100%', maxWidth: '100%', overflow: 'hidden' },
  default: { minWidth: 0, flexShrink: 1, width: '100%', maxWidth: '100%', overflow: 'hidden' },
});

const FAVICON_STACK_SIZE = 18;
const FAVICON_STACK_OVERLAP = 8;
const FAVICON_STACK_MAX = 3;

// Create styles outside component
const createStyles = (colors) => StyleSheet.create({
  sourcesRoot: {
    marginTop: 10,
    marginBottom: 4,
    alignSelf: 'flex-start',
    maxWidth: '100%',
  },
  sourcesPill: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    paddingVertical: 4,
    paddingHorizontal: 2,
    gap: 8,
    ...(Platform.OS === 'web' ? { cursor: 'pointer' } : null),
  },
  faviconStack: {
    position: 'relative',
    flexDirection: 'row',
    alignItems: 'center',
    height: FAVICON_STACK_SIZE,
  },
  faviconStackItem: {
    width: FAVICON_STACK_SIZE,
    height: FAVICON_STACK_SIZE,
    borderRadius: FAVICON_STACK_SIZE / 2,
    borderWidth: 1.5,
    borderColor: colors.solid_color_primary,
    backgroundColor: colors.solid_color_secondary,
    overflow: 'hidden',
  },
  faviconStackImage: {
    width: '100%',
    height: '100%',
  },
  sourcesList: {
    marginTop: 8,
    gap: 2,
    maxWidth: '100%',
  },
  sourceRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 6,
    paddingHorizontal: 8,
    borderRadius: 8,
    maxWidth: '100%',
    ...(Platform.OS === 'web' ? { cursor: 'pointer' } : null),
  },
  sourceRowActive: {
    backgroundColor: colors.solid_color_secondary,
  },
  sourceRowFavicon: {
    width: 16,
    height: 16,
    borderRadius: 4,
    flexShrink: 0,
  },
  sourceRowLabel: {
    flexShrink: 1,
  },
  typingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 20,
  },
  typingDot: {
    width: 4,
    height: 4,
    borderRadius: 2,
    backgroundColor: colors.text_color_secondary,
    marginHorizontal: 2,
    opacity: 0.6,
  },
  waitingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    minHeight: 24,
    paddingVertical: 2,
  },
  waitingOnImageContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 24,
    paddingHorizontal: 4,
  },
  waitingOnImageContainerVertical: {
    height: 40,
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  waitingOnImageIconContainer: {
    position: 'relative',
    width: 24,
    height: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
  waitingOnImageIconContainerVertical: {
    position: 'relative',
    width: 50,
    height: 50,
    alignItems: 'center',
    justifyContent: 'center',
  },
  spinnerRing: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    borderRadius: 50,
    borderWidth: 1,
    borderColor: colors.text_color_secondary,
    borderTopColor: 'transparent',
  },
  waitingTimer: {
    width: 60,
    marginLeft: 12,
  },
  waitingTimerVertical: {
    marginTop: 12,
    marginLeft: 0,
    width: '100%',
    textAlign: 'center',
  },
});

const getBaseUrl = (citationUrl) => {
  try {
      // First try parsing as a URL
      const url = new URL(citationUrl);
      let baseUrl = url.hostname;  // Use hostname instead of origin to avoid port numbers
      
      // Strip common prefixes
      baseUrl = baseUrl
          .replace(/^www\./, '');  // Remove www.
      
      return baseUrl;
  } catch (error) {
      // If URL parsing fails, try manual cleanup
      let cleanUrl = citationUrl
          .replace(/^https?:\/\//, '')     // Remove http:// or https://
          .replace(/^www\./, '')           // Remove www.
          .split('/')[0]                   // Remove paths
          .split('?')[0]                   // Remove query parameters
          .split('#')[0]                   // Remove hash fragments
          .split(':')[0];                  // Remove port numbers
      
      return cleanUrl;
  }
}

const getFaviconUrl = (domain, size = 64) => {
  return `https://www.google.com/s2/favicons?domain=${domain}&sz=${size}`;
};

// ChatGPT-style compact Sources control: stacked favicons + "Sources" label.
// Expands into a clean vertical list instead of a wrap of domain chips.
const SourcesControl = React.memo(({
  citations,
  conversationId,
  messageId,
  selectedCitation,
  styles,
  colors,
  onToggle,
  onSourcePress,
  expanded,
}) => {
  const stackDomains = useMemo(() => {
    const seen = new Set();
    const domains = [];
    for (const citation of citations) {
      const domain = citation?.hostname || citation?.origin || getBaseUrl(citation?.url);
      if (!domain || seen.has(domain)) continue;
      seen.add(domain);
      domains.push(domain);
      if (domains.length >= FAVICON_STACK_MAX) break;
    }
    return domains;
  }, [citations]);

  const stackWidth = stackDomains.length > 0
    ? FAVICON_STACK_SIZE + (stackDomains.length - 1) * (FAVICON_STACK_SIZE - FAVICON_STACK_OVERLAP)
    : 0;

  return (
    <View style={styles.sourcesRoot}>
      <Pressable
        onPress={onToggle}
        style={styles.sourcesPill}
        accessibilityRole="button"
        accessibilityState={{ expanded }}
        accessibilityLabel={`Sources, ${citations.length}`}
        accessibilityHint={expanded ? 'Hide sources' : 'Show sources'}
      >
        {stackDomains.length > 0 && (
          <View style={[styles.faviconStack, { width: stackWidth }]}>
            {stackDomains.map((domain, index) => (
              <View
                key={domain}
                style={[
                  styles.faviconStackItem,
                  {
                    position: index === 0 ? 'relative' : 'absolute',
                    left: index * (FAVICON_STACK_SIZE - FAVICON_STACK_OVERLAP),
                    zIndex: stackDomains.length - index,
                  },
                ]}
              >
                <Image
                  source={getFaviconUrl(domain)}
                  style={styles.faviconStackImage}
                  contentFit="cover"
                />
              </View>
            ))}
          </View>
        )}
        <Text size="s" color="secondary">
          Sources{citations.length > 1 ? ` · ${citations.length}` : ''}
        </Text>
      </Pressable>

      {expanded && (
        <View style={styles.sourcesList} accessibilityRole="list">
          {citations.map((citation, index) => {
            const domain = citation?.hostname || citation?.origin || getBaseUrl(citation?.url);
            const label = citation?.title || domain || citation?.url;
            const isActive = selectedCitation === `${conversationId}.${messageId}.${index}`;
            return (
              <Pressable
                key={citation._id || citation.url || index}
                onPress={() => onSourcePress(index)}
                style={[styles.sourceRow, isActive && styles.sourceRowActive]}
                accessibilityRole="link"
                accessibilityLabel={label}
              >
                {domain ? (
                  <Image
                    source={getFaviconUrl(domain)}
                    style={styles.sourceRowFavicon}
                    contentFit="contain"
                  />
                ) : (
                  <Icon name="link" size={12} color={colors.text_color_tertiary} />
                )}
                <Text
                  size="s"
                  color={isActive ? 'primary' : 'secondary'}
                  numberOfLines={1}
                  style={styles.sourceRowLabel}
                >
                  {label}
                </Text>
                {isActive && (
                  <Icon
                    name="arrow-up-right-from-square"
                    size={10}
                    color={colors.text_color_secondary}
                  />
                )}
              </Pressable>
            );
          })}
        </View>
      )}
    </View>
  );
});
SourcesControl.displayName = 'SourcesControl';

// Memoized waiting indicator with rotating phrases (web)
const WaitingDots = React.memo(({ styles, colors }) => {
  const [currentPhraseIndex, setCurrentPhraseIndex] = useState(0);
  const textOpacity = useRef(new Animated.Value(1)).current;
  const ellipsisOpacity = useRef(new Animated.Value(0.4)).current;

  // Rotate phrases every 3 seconds with fade animation
  useEffect(() => {
    const interval = setInterval(() => {
      // Fade out
      Animated.timing(textOpacity, {
        toValue: 0,
        duration: 200,
        useNativeDriver: true,
      }).start(() => {
        // Change phrase
        setCurrentPhraseIndex(prev => (prev + 1) % WAITING_PHRASES.length);
        // Fade in
        Animated.timing(textOpacity, {
          toValue: 1,
          duration: 200,
          useNativeDriver: true,
        }).start();
      });
    }, 3000);

    return () => clearInterval(interval);
  }, [textOpacity]);

  // Pulsing ellipsis animation
  useEffect(() => {
    const pulseAnimation = Animated.loop(
      Animated.sequence([
        Animated.timing(ellipsisOpacity, {
          toValue: 1,
          duration: 800,
          useNativeDriver: true,
        }),
        Animated.timing(ellipsisOpacity, {
          toValue: 0.4,
          duration: 800,
          useNativeDriver: true,
        }),
      ])
    );
    pulseAnimation.start();
    return () => pulseAnimation.stop();
  }, [ellipsisOpacity]);

  return (
    <View style={styles.waitingContainer}>
      <Animated.View style={{ opacity: textOpacity, flexDirection: 'row', alignItems: 'center' }}>
        <Text size="n" color="secondary">
          {WAITING_PHRASES[currentPhraseIndex]}
        </Text>
        <Animated.Text style={{ opacity: ellipsisOpacity, color: colors.text_color_secondary, fontSize: 14, marginLeft: 2 }}>
          ...
        </Animated.Text>
      </Animated.View>
    </View>
  );
});

// Icon + copy for the generating placeholder, keyed by `isRequestingMedia`.
// Falls back to a generic "media" presentation so future media kinds still
// get a sane placeholder even if we forget to add an entry here.
const MEDIA_WAITING_PRESETS = {
  image: {
    icon: 'mountain-sun',
    longHint: 'High-quality images can take a minute or two.',
  },
  video: {
    icon: 'film',
    longHint: 'Video generations can take a few minutes.',
  },
  audio: {
    icon: 'music',
    longHint: 'Songs can take a couple of minutes to compose.',
  },
  '3d': {
    icon: 'cube',
    longHint: '3D model generations can take a few minutes.',
  },
};

const _resolveWaitingPreset = (mediaType) => {
  const key = String(mediaType || '').toLowerCase();
  return MEDIA_WAITING_PRESETS[key] || {
    icon: 'mountain-sun',
    longHint: 'Generations can take a minute or two.',
  };
};

const WaitingOnImageWrapper = React.memo(({ styles, colors, mediaType }) => {
  const { isXS } = useResponsiveness();

  const wrapperStyles = StyleSheet.create({
    waitingOnImageWrapper: {
      width: isXS ? 300 : 440,
      height: isXS ? 300 : 440,
      ...(Platform.OS === 'web' ? { maxWidth: '90vw', maxHeight: '90vw' } : { maxWidth: '90%', maxHeight: '90%' }),
      borderRadius: 18,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: colors.solid_color_secondary,
      overflow: 'hidden',
      position: 'relative',
    },
  });

  return (
    <View style={wrapperStyles.waitingOnImageWrapper}>
      <WaitingOnImage styles={styles} colors={colors} isVertical mediaType={mediaType} />
    </View>
  );
});

// Memoized waiting image component
const WaitingOnImage = React.memo(({ styles, colors, isVertical, mediaType }) => {
  const spinValue = useRef(new Animated.Value(0)).current;
  const [elapsedTime, setElapsedTime] = useState(0);
  const startTime = useRef(Date.now()).current;
  const preset = _resolveWaitingPreset(mediaType);

  useEffect(() => {
    const spin = Animated.loop(
      Animated.timing(spinValue, {
        toValue: 1,
        duration: 1500,
        useNativeDriver: true,
        easing: Easing.linear,
      })
    );
    spin.start();

    const timer = setInterval(() => {
      const elapsed = (Date.now() - startTime) / 1000;
      setElapsedTime(Math.min(elapsed, 600));
    }, 100);

    return () => {
      spin.stop();
      clearInterval(timer);
    };
  }, []);

  const spin = spinValue.interpolate({
    inputRange: [0, 1],
    outputRange: ['0deg', '360deg']
  });

  return (
    <View style={isVertical ? styles.waitingOnImageContainerVertical : styles.waitingOnImageContainer}>
      <View style={isVertical ? styles.waitingOnImageIconContainerVertical : styles.waitingOnImageIconContainer}>
        <Icon 
          name={preset.icon} 
          size={isVertical ? 26 : 14} 
          color={colors.text_color_secondary}
        />
        <Animated.View 
          style={[
            styles.spinnerRing,
            isVertical && styles.spinnerRingVertical,
            { transform: [{ rotate: spin }] }
          ]} 
        />
      </View>
      <Text 
        style={isVertical ? styles.waitingTimerVertical : styles.waitingTimer}
        size="s"
        color="secondary"
      >
        {elapsedTime.toFixed(1)}s
      </Text>
      {isVertical && elapsedTime >= 30 && (
        <Text
          style={{ marginTop: 6, textAlign: 'center', paddingHorizontal: 24 }}
          size="xs"
          color="tertiary"
        >
          {elapsedTime < 60
            ? 'Still generating…'
            : elapsedTime < 120
              ? preset.longHint
              : 'Hang tight — almost there.'}
        </Text>
      )}
    </View>
  );
});

// Placeholder card shown while the assistant generates an editable artifact
// (document draft / presentation deck). The raw markdown/JSON the model
// streams is an internal format — users should see a clean progress state,
// not slide JSON scrolling by. The artifact opens in its editor on arrival.
const ARTIFACT_WAITING_PRESETS = {
  document: {
    icon: 'file-lines',
    title: 'Writing your document',
    hint: 'It will open in the editor as soon as it’s ready.',
    mobileHint: 'It will be attached here as soon as it’s ready.',
  },
  presentation: {
    icon: 'file-powerpoint',
    title: 'Designing your slides',
    hint: 'The deck will open in the editor as soon as it’s ready.',
    mobileHint: 'The deck will be attached here as soon as it’s ready.',
  },
  spreadsheet: {
    icon: 'file-excel',
    title: 'Building your spreadsheet',
    hint: 'The workbook will open in the editor as soon as it’s ready.',
    mobileHint: 'The workbook will be attached here as soon as it’s ready.',
  },
};

const ArtifactWaitingCard = React.memo(({ colors, kind }) => {
  const preset = ARTIFACT_WAITING_PRESETS[kind] || ARTIFACT_WAITING_PRESETS.document;
  // Editors only open on desktop web; elsewhere the file simply arrives as
  // a chat attachment, so don't promise an editor that won't appear.
  const { canUseDesktopEditors } = require('@/constants/utils/files');
  const hint = canUseDesktopEditors() ? preset.hint : preset.mobileHint;
  const [elapsedTime, setElapsedTime] = useState(0);

  useEffect(() => {
    const startTime = Date.now();
    const timer = setInterval(() => {
      setElapsedTime(Math.min((Date.now() - startTime) / 1000, 600));
    }, 500);
    return () => clearInterval(timer);
  }, []);

  return (
    <View
      accessibilityRole="progressbar"
      accessibilityLabel={preset.title}
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        paddingVertical: 12,
        paddingHorizontal: 14,
        borderRadius: 12,
        borderWidth: 1,
        borderColor: colors.border_color_secondary,
        backgroundColor: colors.solid_color_secondary,
        alignSelf: 'flex-start',
        maxWidth: 420,
        marginVertical: 4,
      }}
    >
      <View style={{ width: 34, height: 34, borderRadius: 10, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.solid_color_primary }}>
        <Icon name={preset.icon} size={16} color={colors.text_color_secondary} />
      </View>
      <View style={{ flexShrink: 1 }}>
        <Text size="n" color="primary" style={{ fontWeight: '600' }}>{preset.title}…</Text>
        <Text size="s" color="secondary" style={{ marginTop: 2 }}>{hint}</Text>
      </View>
      <ActivityIndicator size="small" color={colors.text_color_secondary} style={{ marginLeft: 4 }} />
      <Text size="s" color="tertiary">{Math.floor(elapsedTime)}s</Text>
    </View>
  );
});
ArtifactWaitingCard.displayName = 'ArtifactWaitingCard';

const CONTINUE_DEBOUNCE_MS = 2500;

const ContinueGeneratingButton = React.memo(({ conversationId, messageId, colors }) => {
  const lastEmitAtRef = useRef(0);
  const isTruncated = useChatStore(state => {
    const chat = state.chats.find(c => c.id === conversationId);
    const msg = chat?.messages?.find(m => m.id === messageId);
    return msg?.isTruncated || false;
  });
  const isStreaming = useChatStore(state => {
    const chat = state.chats.find(c => c.id === conversationId);
    const msg = chat?.messages?.find(m => m.id === messageId);
    return msg?.isStreaming || false;
  });
  const userId = useAuthStore(state => state.user?.id);

  const handlePress = useCallback(() => {
    if (!userId || !conversationId || !messageId) return;
    const now = Date.now();
    if (now - lastEmitAtRef.current < CONTINUE_DEBOUNCE_MS) return;
    lastEmitAtRef.current = now;
    socketManager.emitContinueGeneration({ userId, conversationId, messageId });
  }, [userId, conversationId, messageId]);

  if (!isTruncated || isStreaming) return null;

  return (
    <Pressable
      onPress={handlePress}
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        alignSelf: 'flex-start',
        paddingVertical: 6,
        paddingHorizontal: 10,
        marginTop: 8,
        borderRadius: 6,
        borderWidth: 1,
        borderColor: colors.border_color_secondary,
        backgroundColor: colors.solid_color_secondary,
      }}
      accessibilityRole="button"
      accessibilityLabel="Continue generating"
    >
      <Icon name="arrow-rotate-right" size={12} color={colors.text_color_secondary} style={{ marginRight: 6 }} />
      <Text size="s" color="secondary">Continue generating</Text>
    </Pressable>
  );
});
ContinueGeneratingButton.displayName = 'ContinueGeneratingButton';

function MessageContent({ 
  content, 
  conversationId,
  messageId,
  isUser = false,
  messageLayout,
  isStreaming = false, 
  isWaiting = false,
  isTyping = false,
  isError = false,
  hasError = false,
  isDeleted = false,
  citations = [],
  products = [],
  stockQuotes = [],
  places = [],
  map = null,
  isRequestingMedia = null,
  modelIds = [],
  agentIds = [],
  inferenceStatus,
  inferenceStage,
  inferenceElapsedMs,
}) {
  const { colors, theme: themeProp } = useTheme();
  const theme = useMemo(() => ({ colors, theme: themeProp }), [colors, themeProp]);
  const { openModal } = useModalStore(state => state.actions);
  const setSelectedCitation = useChatStore(state => state.setSelectedCitation);
  const selectedCitation = useChatStore(state => state.selectedCitation);
  const setShowSourcesOutputsPanel = useUIStore(state => state.setShowSourcesOutputsPanel);
  const stockChartsEnabled = useAuthStore(
    state => state.user?.preferences?.chat?.stockChartsEnabled !== false
  );
  const [sourcesExpanded, setSourcesExpanded] = useState(false);

  const { useConfirmationModal } = useModalHelper();
  const showConfirmationModal = useConfirmationModal();

  // Show the dedicated media placeholder for any generated-media request
  // (image / video / song / 3D), not just images. Without this, video and
  // audio generations fall back to the generic "thinking" dots and the
  // user can't tell that media is still on the way after stream_end clears
  // the streaming flags but BEFORE the file is wired onto the message.
  const requestedMediaKind = String(isRequestingMedia || '').toLowerCase();
  const isMediaWaitingKind = requestedMediaKind === 'image'
    || requestedMediaKind === 'video'
    || requestedMediaKind === 'audio'
    || requestedMediaKind === '3d';
  const isMediaWaiting = isMediaWaitingKind && isWaiting;

  // Document/presentation generations stream an internal format (Markdown /
  // slide JSON) that gets converted into an editable artifact after the
  // stream ends. Hide the raw stream behind a progress card; the final
  // message content is replaced server-side with a short acknowledgment.
  const isArtifactKind = requestedMediaKind === 'document'
    || requestedMediaKind === 'presentation'
    || requestedMediaKind === 'spreadsheet';
  const isArtifactGenerating = isArtifactKind && !isError && !isDeleted &&
    (isWaiting || isStreaming || isTyping);

  // Memoize styles
  const styles = useMemo(() => 
    createStyles(colors, isError),
    [colors, isError]
  );

  const onToggleSources = useCallback(() => {
    setSourcesExpanded((prev) => {
      const next = !prev;
      // On desktop web, also surface the floating Sources / Outputs panel.
      if (next && Platform.OS === 'web') {
        setShowSourcesOutputsPanel(true);
      }
      return next;
    });
  }, [setShowSourcesOutputsPanel]);

  const onSourcePress = useCallback((index) => {
    if (!conversationId || !messageId) return;

    // First click highlights the inline citation; second click opens the URL.
    const currentCitation = `${conversationId}.${messageId}.${index}`;
    if (selectedCitation === currentCitation) {
      const url = citations[index]?.url;
      if (!url) return;
      showConfirmationModal({
        title: 'External Link',
        message: 'You are about to visit an external website. Please be careful with links from unknown sources.',
        itemName: url,
        confirmText: 'Open Link',
        cancelText: 'Cancel',
        onConfirm: () => {
          if (Platform.OS === 'web') {
            window.open(url, '_blank');
          } else {
            Linking.openURL(url);
          }
        },
      });
    } else {
      setSelectedCitation(conversationId, messageId, index);
    }
  }, [conversationId, messageId, selectedCitation, citations, showConfirmationModal, setSelectedCitation]);

  const citationsSection = useMemo(() => {
    if (!citations?.length || isError || isDeleted) return null;

    return (
      <SourcesControl
        citations={citations}
        conversationId={conversationId}
        messageId={messageId}
        selectedCitation={selectedCitation}
        styles={styles}
        colors={colors}
        expanded={sourcesExpanded}
        onToggle={onToggleSources}
        onSourcePress={onSourcePress}
      />
    );
  }, [
    citations,
    isError,
    isDeleted,
    colors,
    styles,
    onToggleSources,
    onSourcePress,
    selectedCitation,
    conversationId,
    messageId,
    sourcesExpanded,
  ]);

  // Memoize content section
  const contentSection = useMemo(() => {
    if (isArtifactGenerating) {
      return <ArtifactWaitingCard colors={colors} kind={requestedMediaKind} />;
    }

    if (isWaiting) {
      if (isMediaWaiting) {
        return (
          <WaitingOnImageWrapper
            styles={styles}
            colors={colors}
            mediaType={requestedMediaKind}
          />
        );
      }
      // On native (iOS/Android), keep the same rich thinking indicator used by
      // MessageWaiting (icon + status + dots + shimmer) so the placeholder
      // assistant message doesn't collapse into centered orphan dots.
      // On web, show the text-based rotating phrases.
      return Platform.OS !== 'web'
        ? (
          <NativeThinkingIndicator
            inferenceStatus={inferenceStatus}
            inferenceStage={inferenceStage}
            inferenceElapsedMs={inferenceElapsedMs}
          />
        )
        : <WaitingDots styles={styles} colors={colors} />;
    }

    return (
      <MessageContentRenderer
        conversationId={conversationId}
        messageId={messageId}
        content={content}
        theme={theme}
        isStreaming={isStreaming}
        messageLayout={messageLayout}
        isUser={isUser}
        isError={isError}
        isDeleted={isDeleted}
      />
    );
  }, [
    isArtifactGenerating,
    isWaiting,
    isMediaWaiting,
    requestedMediaKind,
    inferenceStatus,
    inferenceStage,
    inferenceElapsedMs,
    content,
    theme,
    isStreaming,
    messageLayout,
    isUser,
    isError,
    styles,
    colors
  ]);

  return (
    <View
      accessibilityLabel="message-content"
      accessibilityState={{
        busy: isStreaming || isWaiting,
        disabled: isError,
      }}
      style={contentContainerStyle}
    >
      {contentSection}
      {!isUser && !isError && !isDeleted && (
        <ContinueGeneratingButton conversationId={conversationId} messageId={messageId} colors={colors} />
      )}
      {!isUser && !isError && !isDeleted && stockChartsEnabled && stockQuotes?.length > 0 && (
        <MessageStockQuotes stockQuotes={stockQuotes} />
      )}
      {!isUser && !isError && !isDeleted && places?.length > 0 && (
        <MessagePlaces places={places} map={map} />
      )}
      {!isUser && !isError && !isDeleted && products?.length > 0 && (
        <MessageProducts products={products} />
      )}
      {citationsSection}
    </View>
  );
}

MessageContent.propTypes = {
  messageId: PropTypes.string.isRequired,
  content: PropTypes.string,
  isUser: PropTypes.bool,
  messageLayout: PropTypes.string,
  isStreaming: PropTypes.bool,
  isWaiting: PropTypes.bool,
  isTyping: PropTypes.bool, // doesn't do anything right now
  isError: PropTypes.bool,
  isDeleted: PropTypes.bool,
  hasError: PropTypes.bool,
  citations: PropTypes.arrayOf(PropTypes.shape({
    url: PropTypes.string,
    hostname: PropTypes.string,
    title: PropTypes.string,
  })),
  products: PropTypes.array,
  stockQuotes: PropTypes.array,
  places: PropTypes.array,
  map: PropTypes.object,
  isRequestingMedia: PropTypes.string, // image, video, audio, pdf, etc
  modelIds: PropTypes.array,
  agentIds: PropTypes.array,
  inferenceStatus: PropTypes.string,
  inferenceStage: PropTypes.string,
  inferenceElapsedMs: PropTypes.number,
};

MessageContent.displayName = 'MessageContent';

export default React.memo(MessageContent); 